import { useState, useEffect, createContext, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import ReactGA from "react-ga4";
import Lenis from "@studio-freight/lenis";
import Header from "./components/templateParts/Header/Header";
import SiteIntro from "./components/templateParts/SiteIntro/SiteIntro";
import useStore from "./hooks/useStore";
import "./layout.scoped.scss";
import HeaderRevamp from "./components/templateParts/Header/HeaderRevamp";
import FooterRevamp from "./components/templateParts/Footer/FooterRevamp";

export const context = createContext();

const Layout = ({ children }) => {
  const { appState, acfOptions, getItems, requestedItems } = useStore();
  // const { introShown } = useIntro()
  const [isOpened, setIsOpened] = useState(false);
  const [introShown, setIntroShown] = useState(false);
  const [scrollLenis, setScrollLenis] = useState(0);
  const [allCategories, setAllCategories] = useState([]);

  const location = useLocation();
  const options = acfOptions();
  // const slug = location.pathname.replaceAll('/', '')
  const gacode = options?.ga_code;

  if (gacode) {
    ReactGA.initialize([
      {
        trackingId: gacode,
      },
    ]);
  }

  const toggleIsOpened = (open = "false") => {
    if (open === "false") {
      setIsOpened(false);
    } else if (open === "true") {
      setIsOpened(true);
    } else if (open === "toggle") {
      setIsOpened((prev) => !prev);
    }
  };

  const toggleScroll = (scroll = false) => {
    if (!lenisInstance) return;

    if (scroll === false) {
      lenisInstance.stop();
    } else {
      lenisInstance.start();
    }
  };

  const [lenisInstance, setLenisInstance] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // use only in desktop
    const isDesktop = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!isDesktop) return;

    const lenis = new Lenis({
      smooth: true,
    });

    setLenisInstance(lenis);

    let frameId;
    const raf = (time) => {
      lenis.raf(time);
      frameId = requestAnimationFrame(raf);
    };
    frameId = requestAnimationFrame(raf);

    // cleanup
    return () => {
      cancelAnimationFrame(frameId);
      lenis.destroy?.();
    };
  }, []);

  const getAllCategories = () => {
    const catRequest = {
      type: "categories",
      params: {
        per_page: 10,
      },
    };

    async function fetchCategories() {
      try {
        await getItems(catRequest);
        return requestedItems(catRequest);
      } catch (error) {
        console.error("Error fetching categories:", error);
        return [];
      }
    }

    return fetchCategories();
  };

  useEffect(() => {
    toggleIsOpened("false");
    // Google Analytics
    if (gacode) {
      ReactGA.send({ hitType: "pageview", page: location.pathname });
    }
  }, [location]);

  useEffect(() => {
    const hashFromSearch = new URLSearchParams(location.search).get("anchor");
    const hash = (location.hash?.slice(1) || hashFromSearch || "").trim();
    const getHeaderOffset = () => document.querySelector("[data-sticky-header]")?.offsetHeight ?? 0;

    // mobile only
    if (!lenisInstance) {
      if (!hash) {
        window.scrollTo(0, 0);
        return;
      }

      let cancelled = false;

      const scrollWhenReady = () => {
        if (cancelled) return;
        const el = document.getElementById(decodeURIComponent(hash));
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
          window.scrollTo({ top, behavior: "auto" });
        } else {
          requestAnimationFrame(scrollWhenReady);
        }
      };

      requestAnimationFrame(scrollWhenReady);
      return () => {
        cancelled = true;
      };
    }

    if (!hash) {
      lenisInstance.scrollTo(0, { immediate: true });
      return;
    }

    let cancelled = false;

    const scrollWhenReady = () => {
      if (cancelled) return;
      const el = document.getElementById(decodeURIComponent(hash));
      if (el) {
        if (!lenisInstance) return;
        lenisInstance.scrollTo(el, { offset: -getHeaderOffset() });
      } else {
        requestAnimationFrame(scrollWhenReady);
      }
    };

    requestAnimationFrame(scrollWhenReady);
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.hash, location.search, lenisInstance]);

  useEffect(() => {
    if (!lenisInstance) return;
    if (isOpened) {
      lenisInstance.stop();
    } else {
      lenisInstance.start();
    }
  }, [isOpened, lenisInstance]);

  useEffect(() => {
    if (!lenisInstance) return;
    if (!introShown) {
      lenisInstance.stop();
    } else {
      lenisInstance.start();
    }
  }, [introShown, lenisInstance]);

  useEffect(() => {
    getAllCategories().then((res) => {
      setAllCategories(res);
    });
  }, []);

  const setIntroComplete = () => {
    setIntroShown(true);
    sessionStorage.setItem("introShown", true);
  };

  const contextValue = useMemo(
    () => ({
      appState,
      isOpened,
      toggleIsOpened,
      lenisInstance,
      introShown,
      setIntroComplete,
      toggleScroll,
      scrollLenis,
    }),
    [
      appState,
      isOpened,
      toggleIsOpened,
      lenisInstance,
      introShown,
      setIntroComplete,
      toggleScroll,
      scrollLenis,
    ]
  );

  useEffect(() => {
    const value = sessionStorage.getItem("introShown");
    if (value) setIntroShown(true);
  }, []);

  useEffect(() => {
    if (!lenisInstance) return;

    const handler = (e) => {
      setScrollLenis(e.animatedScroll);
    };

    lenisInstance.on("scroll", handler);

    return () => {
      lenisInstance.off?.("scroll", handler);
    };
  }, [lenisInstance]);

  return (
    <context.Provider value={contextValue}>
      <SiteIntro />

      <div id="website-wrapper">
        <HeaderRevamp isOpened={isOpened} toggleIsOpened={toggleIsOpened} />
        <div id="website-content">
          <Outlet />
          {children}
        </div>
        <FooterRevamp />
      </div>
    </context.Provider>
  );
};

Layout.propTypes = {
  children: PropTypes.any,
};

export default Layout;
